import { FC, useEffect, useMemo } from "react";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CompetitionInputComponents } from "../CompetitionInputComponents";
import { ButtonVariant, FlexBox, GridBox } from "@brg/ui";
import { Container, ErrorMessage, LabelRedStar, LegendBadge } from "../../style";
import { AnnotationLabel, MainTitle } from "shared/pages/CompetitionApplicationForm/components/styles";
import { useTranslation } from "react-i18next";
import { SendButton } from "shared/modals/styles";
import { CompetitionConsentNames } from "../../types";
import urlApi from "shared/actions/urlApi";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationEditorActions, ApplicationEditorSelector } from "shared/reducers/ApplicationEditorSlice";
import { useGetFieldConfig } from "./hooks/useGetFieldConfig";
import { FormFieldType, FormType } from "shared/modals/ApplicationEditor/constants";
import { FieldInput } from "./components/FieldInput";
import { Lang } from "@brg/api";
import { AppTester } from "supporting";

interface FormProps {
  competitionId?: string,
  projectId?: string,
  nominationId?: string,
  isPreRegistration?: boolean,
  shouldSignConsent?: boolean,
  onSubmit: (data: Record<string, any>) => void;
}

const Form: FC<FormProps> = ({ 
    competitionId, 
    projectId, 
    nominationId,
    isPreRegistration, 
    onSubmit 
}) => {
    const { t, i18n: { language } } = useTranslation();
    const dispatch = useDispatch();
    const {  formId, fields, docs, header, description } = useSelector(ApplicationEditorSelector);
    const action = ApplicationEditorActions;
    const isStart = AppTester.isStartHSE();
    const {
        config: fieldConfigs
    } = useGetFieldConfig(
        [...fields, ...docs], 
        [],
        projectId, 
        nominationId,
    )

    const normalizedFieldConfigs = useMemo(() => {
        return (fieldConfigs ?? []).reduce((acc, fieldConfig, index) => {
            const potentialName = (fieldConfig as any)?.name;
            const fieldName = (fieldConfig as any)?.isOldLegalCheckbox
                ? `old_ppd_${index}`
                : typeof potentialName === 'string' && potentialName.trim().length > 0
                  ? potentialName
                  : `field_${index}`;

            return {
                ...acc,
                [fieldName]: {
                    ...fieldConfig,
                    name: fieldName,
                },
            };
        }, {} as Record<string, any>);
    }, [fieldConfigs]);

    const validationSchema = useMemo(() => yup.object(
        Object.entries(normalizedFieldConfigs).reduce((acc, [fieldName, fieldConfig]) => {
            if (fieldConfig?.validation) {
                acc[fieldName] = fieldConfig.validation;
            }
            return acc;
        }, {} as Record<string, yup.AnySchema>)
    ), [normalizedFieldConfigs]);

    const defaultValues = useMemo(() => {
        return Object.entries(normalizedFieldConfigs).reduce((acc, [fieldName, fieldConfig]) => {
            const fieldType = fieldConfig?.type;
            const initialValue =
                fieldType === FormFieldType.Date ? null
                : fieldType === FormFieldType.SelectMultiple ? []
                : fieldConfig?.isOldLegalCheckbox ? false
                : fieldType === FormFieldType.File ? null
                : fieldType === FormFieldType.LegalDocument ? false
                : '';

            return {
                ...acc,
                [fieldName]: fieldConfig?.defaultValue ?? initialValue,
            };
        }, {} as Record<string, unknown>);
    }, [normalizedFieldConfigs]);

    const submitText = useMemo(() => {
        return isStart ? t('competition.submit_start') :t('competition.submit_form')
    }, [isStart, language]);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isSubmitted },
    } = useForm<Record<string, any>>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onTouched',
        defaultValues:defaultValues ,
    });

    const fetchFormData = async () => {
        if (competitionId) {
            const formData = await urlApi().fetchCompetitionFormData(competitionId, 'custom');
            if (formData.hasFormFields) {
                dispatch(action.setInitialData({ 
                    formId: formData.formId, 
                    fields: formData.fields, 
                    docs: formData.docs, 
                    oldCheckboxes: [],
                    header: formData.header,
                    description: formData.description,
                    formType: FormType.custom }));
            } else {
                const formData = await urlApi().fetchCompetitionFormData(competitionId, 'standard');
                dispatch(action.setInitialData({ 
                    formId: formData.formId, 
                    fields: formData.fields, 
                    docs: formData.docs, 
                    oldCheckboxes: formData.oldCheckboxes,
                    header: formData.header,
                    description: formData.description,
                    formType: FormType.base }));
            }
        }
    }

    useEffect(() => {
        fetchFormData();
    }, [competitionId])

    // const formObject = watch();

    const handleFormSubmit = (data: any) => {
        const result = Object.entries(normalizedFieldConfigs).flatMap(([fieldName, config]) => {
            if (config?.isOldLegalCheckbox) return [];
            let response = data[fieldName];

            if (config?.type === FormFieldType.File && data[fieldName] instanceof FileList && data[fieldName][0]) {
                response = data[fieldName][0].name;
            }

            if (config?.type === FormFieldType.LegalDocument) {
                response = ''
            }

            if ([FormFieldType.SelectOne]?.includes(config?.type)) {
                response = JSON.stringify([Lang(data[fieldName], 'ru')]);
            }

            if ([FormFieldType.SelectMultiple]?.includes(config?.type)) {
                const ruData = data[fieldName].map((item: any) => Lang(item, 'ru'));
                response = JSON.stringify(ruData);
            }

            return [{
                type: config?.type ?? 0,
                header: Lang(config?.header, language),
                response,
                isTagInCrm: config?.isTagInCrm ?? false,
            }];
        });

        onSubmit({
            formId,
            fields: result,
        });
    };

  return (
    <FlexBox direction="column">
    <MainTitle>{Lang(header, language) || t('competition.application_form')}</MainTitle>
    {description && (
        <AnnotationLabel>{Lang(description, language)}</AnnotationLabel>
    )}
    <LegendBadge>
        <LabelRedStar>*</LabelRedStar>
        {`${t('competition.legend_badge')}`}
    </LegendBadge>

    <form noValidate onSubmit={handleSubmit(handleFormSubmit)} encType="multipart/form-data" id="submit-form">
      {Object.entries(normalizedFieldConfigs).map(([fieldName, config]) => {
        const hidden = !!config?.hidden
        //@ts-ignore
        const shouldShowError = isSubmitted || !!errors[fieldName]?.types?.manual;

        if (!config) return null;

        

        return (
            <GridBox key={fieldName} style={{ ...(!!hidden ? { display: 'none'} : {})}}>
                <FieldInput
                    inputProps={{
                        ...config,
                        ...(config?.isOldLegalCheckbox
                            ? {}
                            : { register: register(fieldName as keyof Record<string, any>) }),
                        control,
                        id: fieldName,
                    }}
                />
                {shouldShowError && errors[fieldName as keyof {}] && (
                    <ErrorMessage>
                        {`— `}{errors[fieldName as keyof typeof errors]?.message as string}
                    </ErrorMessage>
                )}
            </GridBox>   
        )
      })}
        <SendButton
            variant={ButtonVariant.Filled}
            fullWidth={isStart}
            onClick={(ev: React.BaseSyntheticEvent) => {
                ev.preventDefault();
                handleSubmit(handleFormSubmit)()
            }}
        >
            {submitText}
        </SendButton>
    </form></FlexBox>
  );
};

export const CompetitionNewCommonForm: FC<{
    competitionId: string,
    projectId: string,
    nominationId: string,
    isPreRegistration?: boolean,
    shouldSignConsent?: boolean,
    onSubmit: (data: any) => void,
}> = ({ competitionId, projectId, nominationId, shouldSignConsent, isPreRegistration, onSubmit }) => {
    return (
        <Container>
            <Form 
                competitionId={competitionId} 
                projectId={projectId} 
                isPreRegistration={isPreRegistration}
                nominationId={nominationId} 
                shouldSignConsent={shouldSignConsent}
                onSubmit={onSubmit} 
            />
        </Container>

    )
}import { FC, useEffect, useMemo } from "react";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CompetitionInputComponents } from "../CompetitionInputComponents";
import { ButtonVariant, FlexBox, GridBox } from "@brg/ui";
import { Container, ErrorMessage, LabelRedStar, LegendBadge } from "../../style";
import { AnnotationLabel, MainTitle } from "shared/pages/CompetitionApplicationForm/components/styles";
import { useTranslation } from "react-i18next";
import { SendButton } from "shared/modals/styles";
import { CompetitionConsentNames } from "../../types";
import urlApi from "shared/actions/urlApi";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationEditorActions, ApplicationEditorSelector } from "shared/reducers/ApplicationEditorSlice";
import { useGetFieldConfig } from "./hooks/useGetFieldConfig";
import { FormFieldType, FormType } from "shared/modals/ApplicationEditor/constants";
import { FieldInput } from "./components/FieldInput";
import { Lang } from "@brg/api";
import { AppTester } from "supporting";

interface FormProps {
  competitionId?: string,
  projectId?: string,
  nominationId?: string,
  isPreRegistration?: boolean,
  shouldSignConsent?: boolean,
  onSubmit: (data: Record<string, any>) => void;
}

const Form: FC<FormProps> = ({ 
    competitionId, 
    projectId, 
    nominationId,
    isPreRegistration, 
    onSubmit 
}) => {
    const { t, i18n: { language } } = useTranslation();
    const dispatch = useDispatch();
    const {  formId, fields, docs, header, description } = useSelector(ApplicationEditorSelector);
    const action = ApplicationEditorActions;
    const isStart = AppTester.isStartHSE();
    const {
        config: fieldConfigs
    } = useGetFieldConfig(
        [...fields, ...docs], 
        [],
        projectId, 
        nominationId,
    )

    const normalizedFieldConfigs = useMemo(() => {
        return (fieldConfigs ?? []).reduce((acc, fieldConfig, index) => {
            const potentialName = (fieldConfig as any)?.name;
            const fieldName = (fieldConfig as any)?.isOldLegalCheckbox
                ? `old_ppd_${index}`
                : typeof potentialName === 'string' && potentialName.trim().length > 0
                  ? potentialName
                  : `field_${index}`;

            return {
                ...acc,
                [fieldName]: {
                    ...fieldConfig,
                    name: fieldName,
                },
            };
        }, {} as Record<string, any>);
    }, [fieldConfigs]);

    const validationSchema = useMemo(() => yup.object(
        Object.entries(normalizedFieldConfigs).reduce((acc, [fieldName, fieldConfig]) => {
            if (fieldConfig?.validation) {
                acc[fieldName] = fieldConfig.validation;
            }
            return acc;
        }, {} as Record<string, yup.AnySchema>)
    ), [normalizedFieldConfigs]);

    const defaultValues = useMemo(() => {
        return Object.entries(normalizedFieldConfigs).reduce((acc, [fieldName, fieldConfig]) => {
            const fieldType = fieldConfig?.type;
            const initialValue =
                fieldType === FormFieldType.Date ? null
                : fieldType === FormFieldType.SelectMultiple ? []
                : fieldConfig?.isOldLegalCheckbox ? false
                : fieldType === FormFieldType.File ? null
                : fieldType === FormFieldType.LegalDocument ? false
                : '';

            return {
                ...acc,
                [fieldName]: fieldConfig?.defaultValue ?? initialValue,
            };
        }, {} as Record<string, unknown>);
    }, [normalizedFieldConfigs]);

    const submitText = useMemo(() => {
        return isStart ? t('competition.submit_start') :t('competition.submit_form')
    }, [isStart, language]);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isSubmitted },
    } = useForm<Record<string, any>>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onTouched',
        defaultValues:defaultValues ,
    });

    const fetchFormData = async () => {
        if (competitionId) {
            const formData = await urlApi().fetchCompetitionFormData(competitionId, 'custom');
            if (formData.hasFormFields) {
                dispatch(action.setInitialData({ 
                    formId: formData.formId, 
                    fields: formData.fields, 
                    docs: formData.docs, 
                    oldCheckboxes: [],
                    header: formData.header,
                    description: formData.description,
                    formType: FormType.custom }));
            } else {
                const formData = await urlApi().fetchCompetitionFormData(competitionId, 'standard');
                dispatch(action.setInitialData({ 
                    formId: formData.formId, 
                    fields: formData.fields, 
                    docs: formData.docs, 
                    oldCheckboxes: formData.oldCheckboxes,
                    header: formData.header,
                    description: formData.description,
                    formType: FormType.base }));
            }
        }
    }

    useEffect(() => {
        fetchFormData();
    }, [competitionId])

    // const formObject = watch();

    const handleFormSubmit = (data: any) => {
        const result = Object.entries(normalizedFieldConfigs).flatMap(([fieldName, config]) => {
            if (config?.isOldLegalCheckbox) return [];
            let response = data[fieldName];

            if (config?.type === FormFieldType.File && data[fieldName] instanceof FileList && data[fieldName][0]) {
                response = data[fieldName][0].name;
            }

            if (config?.type === FormFieldType.LegalDocument) {
                response = ''
            }

            if ([FormFieldType.SelectOne]?.includes(config?.type)) {
                response = JSON.stringify([Lang(data[fieldName], 'ru')]);
            }

            if ([FormFieldType.SelectMultiple]?.includes(config?.type)) {
                const ruData = data[fieldName].map((item: any) => Lang(item, 'ru'));
                response = JSON.stringify(ruData);
            }

            return [{
                type: config?.type ?? 0,
                header: Lang(config?.header, language),
                response,
                isTagInCrm: config?.isTagInCrm ?? false,
            }];
        });

        onSubmit({
            formId,
            fields: result,
        });
    };

  return (
    <FlexBox direction="column">
    <MainTitle>{Lang(header, language) || t('competition.application_form')}</MainTitle>
    {description && (
        <AnnotationLabel>{Lang(description, language)}</AnnotationLabel>
    )}
    <LegendBadge>
        <LabelRedStar>*</LabelRedStar>
        {`${t('competition.legend_badge')}`}
    </LegendBadge>

    <form noValidate onSubmit={handleSubmit(handleFormSubmit)} encType="multipart/form-data" id="submit-form">
      {Object.entries(normalizedFieldConfigs).map(([fieldName, config]) => {
        const hidden = !!config?.hidden
        //@ts-ignore
        const shouldShowError = isSubmitted || !!errors[fieldName]?.types?.manual;

        if (!config) return null;

        

        return (
            <GridBox key={fieldName} style={{ ...(!!hidden ? { display: 'none'} : {})}}>
                <FieldInput
                    inputProps={{
                        ...config,
                        ...(config?.isOldLegalCheckbox
                            ? {}
                            : { register: register(fieldName as keyof Record<string, any>) }),
                        control,
                        id: fieldName,
                    }}
                />
                {shouldShowError && errors[fieldName as keyof {}] && (
                    <ErrorMessage>
                        {`— `}{errors[fieldName as keyof typeof errors]?.message as string}
                    </ErrorMessage>
                )}
            </GridBox>   
        )
      })}
        <SendButton
            variant={ButtonVariant.Filled}
            fullWidth={isStart}
            onClick={(ev: React.BaseSyntheticEvent) => {
                ev.preventDefault();
                handleSubmit(handleFormSubmit)()
            }}
        >
            {submitText}
        </SendButton>
    </form></FlexBox>
  );
};

export const CompetitionNewCommonForm: FC<{
    competitionId: string,
    projectId: string,
    nominationId: string,
    isPreRegistration?: boolean,
    shouldSignConsent?: boolean,
    onSubmit: (data: any) => void,
}> = ({ competitionId, projectId, nominationId, shouldSignConsent, isPreRegistration, onSubmit }) => {
    return (
        <Container>
            <Form 
                competitionId={competitionId} 
                projectId={projectId} 
                isPreRegistration={isPreRegistration}
                nominationId={nominationId} 
                shouldSignConsent={shouldSignConsent}
                onSubmit={onSubmit} 
            />
        </Container>

    )
}